import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  accionsApi,
  esOberta,
  useAccioActivacio,
  useActivacio,
  useCircuit,
  useDestinataris,
  useDocuments,
  useGuardiesAra,
  type ActivacioDto,
  type DestinatariDto,
} from '@/api/activacions';
import { ApiError } from '@/api/client';
import { useMissatgeError } from '@/api/errors';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Carregant, ErrorCarrega, NoTrobat } from '@/pages/EstatsPagina';
import { useMe } from '@/permisos/MeContext';
import { Avatar, BadgeArea, BadgeEstat, BadgeTipus, BotoTrucada } from './comu';
import {
  ctxActivacio,
  etiquetaTipusDesti,
  PARAM_PESTANYA,
  useEntradesXat,
  useReenviaPendents,
  useTempsRealActivacio,
} from './suport';
import {
  ModalDestinataris,
  ModalEscalar,
  ModalExpert,
  ModalInforme,
  ModalItcau,
  ModalNota,
  ModalTancar,
} from './W04Modals';
import {
  PanellActivitat,
  PanellCadenaEscalat,
  PanellDades,
  PanellDestinataris,
  PanellDocumentacio,
} from './W04Panells';
import { BotoSilenci, PanellXat } from './W04Xat';

type ModalObert =
  'nota' | 'expert' | 'escalar' | 'itcau' | 'tancar' | 'informe' | 'destinataris' | null;

const PESTANYES = ['activitat', 'xat', 'persones', 'dades', 'documents'] as const;
type Pestanya = (typeof PESTANYES)[number];

function esPestanya(v: string | null): v is Pestanya {
  return v != null && (PESTANYES as readonly string[]).includes(v);
}

/** Destinataris amb blocs que el backend considera "tècnic" (poden acceptar la gestió). */
const BLOCS_TECNIC = ['TECNIC_GUARDIA', 'TECNIC_EXPERT'];

/** Banda superior quan l'usuari actual és destinatari pendent de confirmar o d'acceptar. */
function BandaDestinatariPropi({ a, propi }: { a: ActivacioDto; propi: DestinatariDto }) {
  const { t } = useTranslation('operacio');
  const missatgeError = useMissatgeError();
  const confirma = useAccioActivacio(a.id, () => accionsApi.confirmar(a.id));
  const accepta = useAccioActivacio(a.id, () => accionsApi.acceptar(a.id));
  const pendentConfirmar = propi.estat !== 'CONFIRMAT';
  const potAcceptar = BLOCS_TECNIC.includes(propi.bloc) && !propi.acceptat;
  if (!pendentConfirmar && !potAcceptar) return null;
  const error = confirma.error ?? accepta.error;
  return (
    <div
      className={`banda-propi${a.critica ? ' banda-propi--critica' : ''}`}
      role="region"
      aria-label={t('detall.propi.titol')}
      data-testid="destinatari-propi"
    >
      <Icona nom={a.critica ? 'exclamationmark-octagon' : 'bell-badge'} />
      <div className="spacer">
        <div className="strong">{t('detall.propi.titol')}</div>
        <div className="t-footnote">
          {pendentConfirmar ? t('detall.propi.textConfirmar') : t('detall.propi.textAcceptar')}
        </div>
        {error ? (
          <div className="error-text" role="alert">
            {missatgeError(error)}
          </div>
        ) : null}
      </div>
      {pendentConfirmar ? (
        <button
          type="button"
          className="btn btn--success btn--s"
          disabled={confirma.isPending}
          onClick={() => confirma.mutate(undefined)}
        >
          <Icona nom="checkmark-circle" />
          {t('detall.accions.confirma')}
        </button>
      ) : null}
      {potAcceptar ? (
        <button
          type="button"
          className="btn btn--primary btn--s"
          disabled={accepta.isPending}
          onClick={() => accepta.mutate(undefined)}
        >
          <Icona nom="hand-raised" />
          {t('detall.accions.accepta')}
        </button>
      ) : null}
    </div>
  );
}

interface AccioMenu {
  clau: string;
  icona: NomIcona;
  text: string;
  fer: () => void;
}

/** Botó «Més accions» amb menú desplegable (tanca amb Escape o clicant fora). */
function MesAccions({ accions }: { accions: AccioMenu[] }) {
  const { t } = useTranslation('operacio');
  const [obert, setObert] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!obert) return;
    const fora = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setObert(false);
    };
    const esc = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setObert(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [obert]);
  if (accions.length === 0) return null;
  return (
    <div className="desplegable" ref={ref}>
      <button
        type="button"
        className="btn"
        aria-haspopup="menu"
        aria-expanded={obert}
        onClick={() => setObert((o) => !o)}
      >
        {t('detall.accions.mes')}
        <Icona nom="chevron-down" />
      </button>
      {obert ? (
        <div className="menu" role="menu" aria-label={t('detall.accions.mes')}>
          {accions.map((ac) => (
            <button
              key={ac.clau}
              type="button"
              role="menuitem"
              className="menu__item"
              onClick={() => {
                setObert(false);
                ac.fer();
              }}
            >
              <Icona nom={ac.icona} />
              {ac.text}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Franja d'estat: confirmacions (i reenviament), tècnic de guàrdia i escalat. */
function FranjaEstat({ a, destinataris }: { a: ActivacioDto; destinataris: DestinatariDto[] }) {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const f = useFormat();
  const { pot } = useMe();
  const missatgeError = useMissatgeError();
  const { pendents, reenvia, potReenviar } = useReenviaPendents(a, destinataris);
  const guardies = useGuardiesAra(pot('cai.guardies', 'CONSULTAR'));
  const circuit = useCircuit(a.serveiIds[0], pot('cai.circuits', 'CONSULTAR'));
  const [ara] = useState(() => Date.now());
  const confirmats = destinataris.filter((d) => d.estat === 'CONFIRMAT').length;
  const total = destinataris.length;
  const pct = total > 0 ? Math.round((confirmats / total) * 100) : 0;

  const tecnic = destinataris
    .filter((d) => d.bloc === 'TECNIC_GUARDIA')
    .sort((x, y) => x.nivell - y.nivell)[0];
  const areaGuardia = guardies.data?.departaments
    .flatMap((d) => d.arees)
    .find((ar) => ar.id === a.areaId);
  const telefon =
    tecnic && areaGuardia?.tecnic?.titularId === tecnic.usuariId
      ? areaGuardia?.tecnic?.titularTelefon
      : null;

  const nivellActual = Math.max(1, a.nivellEscalat);
  const nivells = [...(circuit.data?.nivells ?? [])].sort((x, y) => x.ordre - y.ordre);
  const seguent = nivells.find((n) => n.ordre === nivellActual + 1);
  // Moment previst del nivell següent: espera acumulada del circuit des de l'obertura.
  const esperaSeguent = nivells
    .filter((n) => seguent && n.ordre <= seguent.ordre)
    .reduce((s, n) => s + n.esperaSeg, 0);
  const minutsSeguent = seguent
    ? Math.round((new Date(a.createdAt).getTime() + esperaSeguent * 1000 - ara) / 60000)
    : null;

  let escalat: ReactNode;
  if (!esOberta(a.estat)) escalat = t('detall.franja.tancada');
  else if (seguent && pendents.length > 0) {
    escalat = (
      <>
        {t('detall.franja.nivellSeguent', {
          actual: nivellActual,
          seguent: seguent.ordre,
          desti: etiquetaTipusDesti(seguent.tipusDesti, t),
        })}{' '}
        <strong className="c-incident">
          {minutsSeguent != null && minutsSeguent > 0
            ? t('detall.franja.enMinuts', { minuts: minutsSeguent })
            : t('detall.franja.imminent')}
        </strong>
      </>
    );
  } else if (pendents.length === 0 && total > 0) escalat = t('detall.franja.senseEscalat');
  else escalat = t('detall.nivell', { nivell: nivellActual });

  const horaTecnic = tecnic?.confirmatAt ?? null;
  return (
    <div className="franja" data-testid="franja-estat">
      <div className="panel franja__targeta">
        <div className="progres__cap">
          <span className="dim strong">{t('detall.franja.confirmats')}</span>
          <strong>{t('detall.deTotal', { valor: confirmats, total })}</strong>
        </div>
        <span
          className="progress"
          role="progressbar"
          aria-label={t('detall.franja.confirmats')}
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i style={{ width: `${pct}%`, background: pct === 100 ? 'var(--green)' : undefined }} />
        </span>
        {potReenviar ? (
          <button
            type="button"
            className="enllac"
            disabled={reenvia.isPending}
            onClick={() => reenvia.mutate(undefined)}
          >
            {t('detall.accions.reenvia', { count: pendents.length })}
          </button>
        ) : null}
        {reenvia.isSuccess ? (
          <span className="t-caption dim" role="status">
            {t('detall.reenviat', { count: reenvia.data.enviaments })}
          </span>
        ) : null}
        {reenvia.error ? (
          <span className="error-text" role="alert">
            {missatgeError(reenvia.error)}
          </span>
        ) : null}
      </div>
      <div className="panel franja__targeta franja__targeta--fila">
        <Avatar nom={tecnic?.nom} mida="m" />
        <div className="list-row__text">
          <div className="list-row__label strong">{tecnic?.nom ?? t('tauler.senseTecnic')}</div>
          <div className="list-row__sub">
            {tc('blocDestinatari.TECNIC_GUARDIA')}
            {tecnic
              ? horaTecnic
                ? ` · ${t('detall.franja.haConfirmat', { hora: f.hora(horaTecnic) })}`
                : ` · ${tc(`estatDestinatari.${tecnic.estat}`)}`
              : ''}
          </div>
        </div>
        {tecnic ? <BotoTrucada nom={tecnic.nom} telefon={telefon} /> : null}
      </div>
      <div className="panel franja__targeta">
        <span className="dim strong t-footnote">{t('detall.franja.escalat')}</span>
        <span className="t-subhead">{escalat}</span>
      </div>
    </div>
  );
}

/**
 * Panell d'una pestanya. Tots es mantenen muntats (amagats amb `hidden`) perquè no es perdi
 * l'estat en canviar de pestanya (p. ex. el text a mig escriure de la conversa).
 */
function PanellPestanya({
  p,
  actual,
  idBase,
  children,
}: {
  p: Pestanya;
  actual: Pestanya;
  idBase: string;
  children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panell-${p}`}
      aria-labelledby={`${idBase}-tab-${p}`}
      hidden={p !== actual}
      className="detall__panell"
      data-pestanya={p}
    >
      {children}
    </div>
  );
}

/** Pestanyes del detall (tablist accessible: fletxes esquerra/dreta, Inici i Fi). */
function Pestanyes({
  actual,
  onCanvia,
  etiquetes,
  idBase,
}: {
  actual: Pestanya;
  onCanvia: (p: Pestanya) => void;
  etiquetes: Record<Pestanya, string>;
  idBase: string;
}) {
  const { t } = useTranslation('operacio');
  const refs = useRef<Partial<Record<Pestanya, HTMLButtonElement | null>>>({});
  const tecla = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = PESTANYES.indexOf(actual);
    const desti =
      e.key === 'ArrowRight'
        ? PESTANYES[(i + 1) % PESTANYES.length]
        : e.key === 'ArrowLeft'
          ? PESTANYES[(i - 1 + PESTANYES.length) % PESTANYES.length]
          : e.key === 'Home'
            ? PESTANYES[0]
            : e.key === 'End'
              ? PESTANYES[PESTANYES.length - 1]
              : undefined;
    if (!desti) return;
    e.preventDefault();
    onCanvia(desti);
    refs.current[desti]?.focus();
  };
  return (
    <div
      className="pestanyes detall__pestanyes"
      role="tablist"
      aria-label={t('detall.pestanyes.etiqueta')}
      onKeyDown={tecla}
    >
      {PESTANYES.map((p) => (
        <button
          key={p}
          ref={(el) => {
            refs.current[p] = el;
          }}
          type="button"
          role="tab"
          id={`${idBase}-tab-${p}`}
          aria-controls={`${idBase}-panell-${p}`}
          aria-selected={p === actual}
          tabIndex={p === actual ? 0 : -1}
          className="pestanyes__item"
          onClick={() => onCanvia(p)}
        >
          {etiquetes[p]}
        </button>
      ))}
    </div>
  );
}

/**
 * W-04 Detall d'activació (panell dret del mestre-detall d'Activacions): capçalera amb accions,
 * franja d'estat i cinc pestanyes (Activitat, Xat, Persones, Dades, Documents). L'activitat és
 * només la cronologia d'accions; la conversa (missatges i imatges) i la caixa per escriure són a
 * la pestanya Xat. La pestanya activa es reflecteix a l'URL (`?pestanya=xat`).
 */
export function W04DetallActivacio({ id }: { id: number }) {
  const { t } = useTranslation('operacio');
  const f = useFormat();
  const { pot, me } = useMe();
  const activacio = useActivacio(id);
  const destinataris = useDestinataris(id);
  const documents = useDocuments(id);
  const [modal, setModal] = useState<ModalObert>(null);
  const { entrades: entradesXat } = useEntradesXat(id);
  // La pestanya activa viu a l'URL (`?pestanya=xat`) perquè s'hi pugui enllaçar directament
  // (p. ex. des de l'avís d'un missatge nou) i perquè els avisos sàpiguen si s'està veient el xat.
  const [params, setParams] = useSearchParams();
  const valorParam = params.get(PARAM_PESTANYA);
  const pestanya: Pestanya = esPestanya(valorParam) ? valorParam : 'activitat';
  const setPestanya = (p: Pestanya) =>
    setParams(
      (anteriors) => {
        const nous = new URLSearchParams(anteriors);
        if (p === 'activitat') nous.delete(PARAM_PESTANYA);
        else nous.set(PARAM_PESTANYA, p);
        return nous;
      },
      { replace: true },
    );
  useTempsRealActivacio(Number.isFinite(id) ? id : null);

  if (!Number.isFinite(id)) return <NoTrobat />;
  if (activacio.isLoading) return <Carregant />;
  if (activacio.error instanceof ApiError && activacio.error.status === 404) return <NoTrobat />;
  if (activacio.isError || !activacio.data) {
    return <ErrorCarrega onReintenta={() => void activacio.refetch()} />;
  }
  const a = activacio.data;
  const ctx = ctxActivacio(a);
  const oberta = esOberta(a.estat);
  const ds = destinataris.data ?? [];
  const propi = me ? ds.find((d) => d.usuariId === me.id) : undefined;
  const esActivador = me != null && a.activadorId === me.id;
  const tanca = () => setModal(null);
  const potModificar = oberta && pot('cai.activacions', 'MODIFICAR', ctx);
  const potEscalar = oberta && pot('cai.activacions.escalat', 'ALTA', ctx);
  const potTancar = oberta && (pot('cai.activacions.tancament', 'ALTA', ctx) || esActivador);

  const mes: AccioMenu[] = [
    ...(potModificar
      ? [
          {
            clau: 'nota',
            icona: 'pencil' as const,
            text: t('detall.accions.afegeixNota'),
            fer: () => setModal('nota'),
          },
          {
            clau: 'destinataris',
            icona: 'person-2' as const,
            text: t('detall.accions.afegeixDestinataris'),
            fer: () => setModal('destinataris'),
          },
        ]
      : []),
    ...(oberta && pot('cai.activacions.expert', 'ALTA', ctx)
      ? [
          {
            clau: 'expert',
            icona: 'person-check' as const,
            text: t('detall.accions.activaExpert'),
            fer: () => setModal('expert'),
          },
        ]
      : []),
    ...(pot('cai.activacions.itcau', 'MODIFICAR', ctx)
      ? [
          {
            clau: 'itcau',
            icona: 'link' as const,
            text: t('detall.accions.vinculaItcau'),
            fer: () => setModal('itcau'),
          },
        ]
      : []),
    {
      clau: 'informe',
      icona: 'doc-export',
      text: t('detall.accions.informe'),
      fer: () => setModal('informe'),
    },
  ];

  const etiquetes: Record<Pestanya, string> = {
    activitat: t('detall.pestanyes.activitat'),
    xat: t('detall.pestanyes.xat', { count: entradesXat.length }),
    persones: t('detall.pestanyes.persones', { count: ds.length }),
    dades: t('detall.pestanyes.dades'),
    documents: t('detall.pestanyes.documents', { count: documents.data?.length ?? 0 }),
  };
  const idBase = `detall-${a.id}`;

  return (
    <section
      className={`detall${pestanya === 'xat' ? ' detall--xat' : ''}`}
      data-screen="W-04"
      aria-labelledby={`${idBase}-titol`}
    >
      <Link className="toolbar__seccio mestre__torna" to="/activacions">
        {t('comu:nav.torna', { seccio: t('titols.activacions') })}
      </Link>
      <header className="detall__cap">
        <div className="hstack gap-8 detall__badges">
          <BadgeTipus tipus={a.tipus} critica={a.critica} />
          <BadgeArea tipus={a.areaTipus} />
          <BadgeEstat estat={a.estat} />
          <span className="codi">
            {a.codi} ·{' '}
            {t('detall.obertaPer', {
              hora: f.dataHora(a.createdAt),
              persona: a.activador ?? '—',
            })}
          </span>
        </div>
        <div className="detall__fila">
          <div className="detall__titol">
            <h2 id={`${idBase}-titol`}>{a.titol}</h2>
            <span className="dim">
              {[
                a.area,
                a.servei,
                a.zona ?? a.zonaText,
                a.itcau?.ref ? t('detall.itcauRef', { ref: a.itcau.ref }) : t('detall.itcauSense'),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <div className="detall__accions">
            {potEscalar ? (
              <button type="button" className="btn" onClick={() => setModal('escalar')}>
                <Icona nom="arrow-up" />
                {t('detall.accions.escala')}
              </button>
            ) : null}
            <BotoSilenci a={a} />
            <MesAccions accions={mes} />
            {potTancar ? (
              <button type="button" className="btn btn--primary" onClick={() => setModal('tancar')}>
                <Icona nom="checkmark" />
                {t('detall.accions.tanca')}
              </button>
            ) : null}
          </div>
        </div>
      </header>
      {oberta && propi ? <BandaDestinatariPropi a={a} propi={propi} /> : null}
      <FranjaEstat a={a} destinataris={ds} />
      <div className="panel detall__targeta">
        <Pestanyes actual={pestanya} onCanvia={setPestanya} etiquetes={etiquetes} idBase={idBase} />
        <PanellPestanya p="activitat" actual={pestanya} idBase={idBase}>
          <PanellActivitat a={a} />
        </PanellPestanya>
        <PanellPestanya p="xat" actual={pestanya} idBase={idBase}>
          <PanellXat a={a} actiu={pestanya === 'xat'} />
        </PanellPestanya>
        <PanellPestanya p="persones" actual={pestanya} idBase={idBase}>
          <div className="detall__persones">
            <PanellDestinataris
              a={a}
              destinataris={ds}
              onAfegeix={potModificar ? () => setModal('destinataris') : undefined}
            />
            <PanellCadenaEscalat a={a} destinataris={ds} />
          </div>
        </PanellPestanya>
        <PanellPestanya p="dades" actual={pestanya} idBase={idBase}>
          <PanellDades a={a} destinataris={ds} />
        </PanellPestanya>
        <PanellPestanya p="documents" actual={pestanya} idBase={idBase}>
          <PanellDocumentacio a={a} />
        </PanellPestanya>
      </div>
      {modal === 'nota' ? <ModalNota activacio={a} onTanca={tanca} /> : null}
      {modal === 'expert' ? <ModalExpert activacio={a} onTanca={tanca} /> : null}
      {modal === 'escalar' ? <ModalEscalar activacio={a} onTanca={tanca} /> : null}
      {modal === 'itcau' ? <ModalItcau activacio={a} onTanca={tanca} /> : null}
      {modal === 'tancar' ? <ModalTancar activacio={a} onTanca={tanca} /> : null}
      {modal === 'informe' ? <ModalInforme activacio={a} onTanca={tanca} /> : null}
      {modal === 'destinataris' ? (
        <ModalDestinataris activacio={a} destinataris={ds} onTanca={tanca} />
      ) : null}
    </section>
  );
}
